"use client";

import React from "react";
import { ErrorMessage } from "@hookform/error-message";
import { Controller, RegisterOptions } from "react-hook-form";
import Select, {
  FormatOptionLabelMeta,
  Props as ReactSelectProps,
} from "react-select";
import CreatableSelect from "react-select/creatable";

import { cn, onBlurWorkaround } from "@app/utils";

import { VALIDATIONS } from "@app/constants";
import { SelectOption } from "@app/types";
import {
  getSelectStyles,
  renderErrorMessage,
  usePlaceholder,
} from "@app/utils";

import { useFormContext } from "./Form";
import { FormItem } from "./FormItem";
import { ReadOnlyField } from "./ReadOnlyField";
import { createVariants } from "../Create";

type SingleValueDisplayProps = {
  selectedOption: SelectOption;
  className?: string;
  getOptionLabel?: (option: SelectOption) => string;
  formatOptionLabel?: (
    option: any,
    { context, inputValue }: FormatOptionLabelMeta<any>
  ) => React.ReactNode;
  readOnly?: boolean;
};

type MultiValueDisplayProps = {
  selectedOptions: SelectOption[];
  getOptionLabel?: (option: SelectOption) => string;
  formatOptionLabel?: (
    option: any,
    { context, inputValue }: FormatOptionLabelMeta<any>
  ) => React.ReactNode;
  readOnly?: boolean;
};

type DisplayProps = {
  value: SelectOption | SelectOption[];
  type: "single" | "multi";
  displayStyle?: string;
  getOptionLabel?: (option: SelectOption) => string;
  formatOptionLabel?: (
    option: any,
    { context, inputValue }: FormatOptionLabelMeta<any>
  ) => React.ReactNode;
  placeholder?: React.ReactNode;
  readOnly?: boolean;
};

export interface SelectFieldProps<OptionType = unknown>
  extends Omit<ReactSelectProps, "onChange"> {
  label?: React.ReactNode;
  name: string;
  formItemProps?: React.ComponentProps<typeof FormItem>;
  isDisabled?: boolean;
  autoComplete?: string;
  rules?: Omit<
    RegisterOptions<any, string>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  creatable?: boolean;
  disableBottomBorder?: boolean;
  displayStyle?: string;
  readOnly?: boolean;
  renderDisplayComponent?: (props: any) => React.JSX.Element;
  containerClass?: string;
  onChange?: (value: OptionType | null) => void;
  renderErrorComponent?: React.ComponentProps<typeof ErrorMessage>["render"];
  flexDirection?: "row" | "column";
  labelOnly?: boolean;
}

const variants = {
  container: "w-full text-sm",
  readOnlyContainer: "w-full text-sm",
  placeholder: "text-gray-dark",
};

const SingleValueDisplay = ({
  className,
  selectedOption,
  getOptionLabel,
  formatOptionLabel,
  readOnly,
}: SingleValueDisplayProps) => {
  let label;
  if (formatOptionLabel) {
    label = formatOptionLabel(selectedOption, undefined);
  } else if (getOptionLabel) {
    label = getOptionLabel(selectedOption);
  } else {
    label = selectedOption.label;
  }

  return (
    <div
      className={`${readOnly ? variants.readOnlyContainer : variants.container} ${className ? className : null}`}
    >
      {label}
    </div>
  );
};

const MultiValueDisplay = ({
  selectedOptions,
  getOptionLabel,
  formatOptionLabel,
  readOnly,
}: MultiValueDisplayProps) => {
  const getLabel = (selectedOption: SelectOption) => {
    let label;
    if (formatOptionLabel) {
      label = formatOptionLabel(selectedOption, undefined);
    } else if (getOptionLabel) {
      label = getOptionLabel(selectedOption);
    } else {
      label = selectedOption.label;
    }
    return label;
  };

  return (
    <div className={readOnly ? variants.readOnlyContainer : variants.container}>
      {selectedOptions.map(getLabel).join(", ")}
    </div>
  );
};

const DefaultDisplay = ({
  displayStyle,
  value,
  type,
  placeholder,
  getOptionLabel,
  formatOptionLabel,
  readOnly,
}: DisplayProps) => {
  const defaultPlaceholder = usePlaceholder(placeholder);

  if (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && !value?.length)
  )
    return (
      <div
        className={cn(
          `${readOnly ? variants.readOnlyContainer : variants.container} ${displayStyle ? displayStyle : null}`
        )}
      >
        <span className={variants.placeholder}>{defaultPlaceholder}</span>
      </div>
    );

  // conditionals check both `type` and `value` to satisfy TypeScript rules
  // without checking `value`, TypeScript can't tell if `value` is of type Option or Option[]
  // alternate solution is type assertion in the `value` passed to selectedOption
  if (type === "single" && !Array.isArray(value)) {
    return (
      <SingleValueDisplay
        readOnly={readOnly}
        className={displayStyle ? displayStyle : null}
        selectedOption={value}
        getOptionLabel={getOptionLabel}
        formatOptionLabel={formatOptionLabel}
      />
    );
  } else if (type === "multi" && Array.isArray(value)) {
    return (
      <MultiValueDisplay
        readOnly={readOnly}
        selectedOptions={value}
        getOptionLabel={getOptionLabel}
        formatOptionLabel={formatOptionLabel}
      />
    );
  } else {
    console.error(value);
    return <span>Error: Invalid type or value</span>;
  }
};

export function SelectField<OptionType>(props: SelectFieldProps<OptionType>) {
  const {
    creatable,
    rules,
    name,
    label,
    formItemProps,
    styles,
    classNames,
    isDisabled,
    disableBottomBorder,
    displayStyle,
    readOnly = false,
    containerClass,
    renderErrorComponent,
    flexDirection = "row",
    labelOnly = false,
    ...selectProps
  } = props;

  const {
    isEditing,
    control,
    formState: { errors },
  } = useFormContext();

  if (!control) {
    throw new Error("SelectField must be used within a Form component");
  }

  const formItemPropsWithOverrides = {
    label:
      typeof label === "string" && rules?.required && isEditing
        ? VALIDATIONS.styleLabel(label)
        : label,
    labelOnly: labelOnly,
    border: false,
    ...formItemProps,
  };
  const SelectElement = creatable ? CreatableSelect : Select;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const display =
          // If SelectField received a render prop AND `field.value` is not empty, render the custom display component
          props.renderDisplayComponent ? (
            // &&
            // ((!Array.isArray(field.value) && field.value !== undefined && field.value !== null) ||
            //   (Array.isArray(field.value) && field.value?.length))
            props.renderDisplayComponent(field)
          ) : (
            // Otherwise, render the default display component
            <DefaultDisplay
              readOnly={readOnly}
              displayStyle={displayStyle}
              type={props.isMulti ? "multi" : "single"}
              value={field.value}
              getOptionLabel={selectProps.getOptionLabel}
              formatOptionLabel={selectProps.formatOptionLabel}
              placeholder={selectProps.placeholder}
            />
          );

        const selectInput = (
          <SelectElement
            isClearable
            {...field}
            {...selectProps}
            onChange={(value, _) => {
              field.onChange(value);
              selectProps.onChange &&
                selectProps.onChange(value as unknown as OptionType);
            }}
            menuPlacement="auto"
            isDisabled={isDisabled ? isDisabled : !isEditing}
            {...getSelectStyles({ styles, classNames })}
            onBlur={onBlurWorkaround}
          />
        );

        return (
          <div className={cn(createVariants.label.base, containerClass)}>
            {readOnly ? (
              <ReadOnlyField
                flexDirection={flexDirection}
                label={formItemPropsWithOverrides.label}
              >
                {display}
              </ReadOnlyField>
            ) : (
              <FormItem
                {...formItemPropsWithOverrides}
                className={
                  flexDirection === "row" ? "flex items-start" : "flex flex-col"
                }
              >
                {!isDisabled && isEditing ? selectInput : display}
              </FormItem>
            )}
            <ErrorMessage
              errors={errors}
              name={field.name}
              render={renderErrorComponent ?? renderErrorMessage}
            />
          </div>
        );
      }}
    />
  );
}
SelectField.displayName = "SelectField";
